import React from "react";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useLocation, useNavigate } from "react-router-dom";

import PageHeader from "../../../Components/PageHeader/PageHeader";
import type { SurveyItem } from "../../../@types/portalAluno";
import {
  MenuPageFeedback,
  MenuPageLoading,
} from "../MenuShared/MenuPageState";
import useSurveys from "../../../hooks/PortalAluno/useSurveys";
import MenuPageBackLink from "../MenuShared/MenuPageBackLink";
import "../Notifications/styles.css";
import "./styles.css";

const SurveysPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, error, isError, isLoading, isRefetching, refetch } =
    useSurveys();

  if (isLoading) {
    return (
      <section className="content-page surveys-page">
        <MenuPageLoading label="Carregando enquetes e pesquisas" />
      </section>
    );
  }

  if (isError && !data) {
    return (
      <section className="content-page surveys-page">
        <PageHeader title="Enquetes e Pesquisas" />
        <MenuPageFeedback
          title="Não foi possível carregar as enquetes"
          message={
            error instanceof Error && error.message !== "Network Error"
              ? error.message
              : "Verifique sua conexão e tente novamente."
          }
          onRetry={() => refetch()}
        />
      </section>
    );
  }

  const items = data ?? [];

  const openSurvey = (item: SurveyItem) => {
    if (item.kind === "external") {
      if (item.externalUrl) {
        window.open(item.externalUrl, "_blank", "noopener,noreferrer");
      }
      return;
    }

    navigate(`/app/menu/enquetes/avaliacao/${item.id}`, {
      state: { from: "/app/menu/enquetes", fromKey: location.key },
    });
  };

  return (
    <section
      className="content-page surveys-page"
      aria-label="Enquetes e Pesquisas"
    >
      <MenuPageBackLink />

      <div className="menu-page-heading-row">
        <PageHeader
          title="Enquetes e Pesquisas"
          description="Participe das avaliações e pesquisas disponíveis para o seu perfil."
        />
        <button
          type="button"
          className="menu-page-refresh"
          onClick={() => refetch()}
          disabled={isRefetching}
        >
          <RefreshRoundedIcon aria-hidden="true" />
          <span>{isRefetching ? "Atualizando…" : "Atualizar"}</span>
        </button>
      </div>

      {items.length > 0 ? (
        <div className="surveys-page__grid">
          {items.map((item) => {
            const isUnavailable =
              item.kind === "external" && !item.externalUrl;

            return (
              <article
                key={`${item.kind}-${item.id}`}
                className="survey-card"
              >
                <div className="survey-card__icon" aria-hidden="true">
                  {item.kind === "external" ? (
                    <LaunchRoundedIcon />
                  ) : (
                    <AssignmentOutlinedIcon />
                  )}
                </div>
                <span className="survey-card__type">
                  {item.kind === "external" ? "Pesquisa externa" : "Enquete"}
                </span>
                <h2>{item.title}</h2>
                <p>
                  {item.description ??
                    "Sua participação ajuda a aprimorar os conteúdos e treinamentos."}
                </p>
                {item.completed && (
                  <span className="survey-card__completed">
                    <CheckCircleRoundedIcon aria-hidden="true" />
                    Respondida
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => openSurvey(item)}
                  disabled={isUnavailable}
                >
                  {isUnavailable
                    ? "Link indisponível"
                    : item.completed
                    ? "Ver avaliação"
                    : "Responder agora"}
                </button>
              </article>
            );
          })}
        </div>
      ) : (
        <MenuPageFeedback
          empty
          title="Nenhuma enquete disponível"
          message="Quando uma nova enquete ou pesquisa estiver disponível, ela aparecerá aqui."
        />
      )}
    </section>
  );
};

export default SurveysPage;
